/**
 * Training Requirements Page - Admin/HR view for managing training hour requirements
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { learningApi } from '../api/learningApi'
import adminApi from '@/modules/admin/api/adminApi'
import type { TrainingRequirement, CreateTrainingRequirementInput, UpdateTrainingRequirementInput } from '../types/learning.types'
import { useAuth } from '@/core/auth/AuthContext'
import LoadingPage from '@/shared/components/LoadingPage'
import toast from 'react-hot-toast'

const TrainingRequirementsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentYear = new Date().getFullYear()

  // Check if user is admin or HR (by role or division)
  const isAdminOrHR = user?.roles?.some(r => r.name === 'admin' || r.name === 'hr') ||
    user?.division_name?.toLowerCase() === 'human resources'

  // Redirect if not admin or HR
  useEffect(() => {
    if (user && !isAdminOrHR) {
      navigate('/learning')
    }
  }, [user, isAdminOrHR, navigate])

  // State
  const [page, setPage] = useState(1)
  const [yearFilter, setYearFilter] = useState<number>(currentYear)
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [editingRequirement, setEditingRequirement] = useState<TrainingRequirement | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<TrainingRequirement | null>(null)

  // Form state
  const [formData, setFormData] = useState<CreateTrainingRequirementInput>({
    employee_id: '',
    year: currentYear,
    category_id: null,  // null means "general" (any category)
    required_hours: 40,
  })
  const [bulkFormData, setBulkFormData] = useState({
    employee_ids: [] as string[],
    year: currentYear,
    category_id: null as string | null,  // null means "general" (any category)
    required_hours: 40,
  })
  const [editFormData, setEditFormData] = useState<UpdateTrainingRequirementInput>({
    required_hours: 0,
    category_id: null,
  })

  const limit = 20
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i + 1)

  // Fetch requirements
  const { data, isLoading } = useQuery({
    queryKey: ['trainingRequirements', page, yearFilter],
    queryFn: () => learningApi.getRequirements({
      page,
      limit,
      year: yearFilter,
    }),
    enabled: isAdminOrHR,
  })

  // Fetch users for the dropdown
  const { data: usersData } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => adminApi.getUsers({ page: 1, limit: 100 }),
    enabled: isAdminOrHR && (showCreateModal || showBulkModal || !!editingRequirement),
  })

  // Fetch categories for the dropdown
  const { data: categoriesData } = useQuery({
    queryKey: ['trainingCategories'],
    queryFn: () => learningApi.getCategories(),
    enabled: isAdminOrHR && (showCreateModal || showBulkModal || !!editingRequirement),
  })

  const requirements = data?.data || []
  const categories = categoriesData?.data || []
  const pagination = data?.pagination
  const users = usersData?.data || []

  // Sort users alphabetically by name
  const sortedUsers = [...users].sort((a, b) =>
    (a.full_name || a.email).localeCompare(b.full_name || b.email)
  )

  // Sort categories alphabetically by name
  const sortedCategories = [...categories].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  // Filter by search
  const filteredRequirements = searchQuery
    ? requirements.filter(req =>
        req.employee.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.employee.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.employee.department?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : requirements

  // Calculate summary stats
  const totalEmployees = requirements.length
  const completedCount = requirements.filter(r => r.is_completed).length
  const inProgressCount = requirements.filter(r => !r.is_completed && r.completion_percentage > 0).length

  // Mutations
  const createMutation = useMutation({
    mutationFn: learningApi.createRequirement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingRequirements'] })
      toast.success('Training requirement created successfully')
      setShowCreateModal(false)
      setFormData({ employee_id: '', year: currentYear, category_id: null, required_hours: 40 })
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to create requirement')
    },
  })

  const bulkCreateMutation = useMutation({
    mutationFn: learningApi.bulkCreateRequirements,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['trainingRequirements'] })
      toast.success(`Created ${data.created_count} training requirements`)
      setShowBulkModal(false)
      setBulkFormData({ employee_ids: [], year: currentYear, category_id: null, required_hours: 40 })
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to create requirements')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTrainingRequirementInput }) =>
      learningApi.updateRequirement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingRequirements'] })
      toast.success('Training requirement updated successfully')
      setEditingRequirement(null)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to update requirement')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: learningApi.deleteRequirement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingRequirements'] })
      toast.success('Training requirement deleted successfully')
      setDeleteConfirm(null)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to delete requirement')
    },
  })

  // Handlers
  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.employee_id) {
      toast.error('Please select an employee')
      return
    }
    createMutation.mutate(formData)
  }

  const handleBulkCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (bulkFormData.employee_ids.length === 0) {
      toast.error('Please select at least one employee')
      return
    }
    bulkCreateMutation.mutate(bulkFormData)
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingRequirement) {
      updateMutation.mutate({
        id: editingRequirement.id,
        data: editFormData,
      })
    }
  }

  const handleDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.id)
    }
  }

  const openEditModal = (req: TrainingRequirement) => {
    setEditingRequirement(req)
    setEditFormData({
      required_hours: req.required_hours,
      category_id: req.category?.id ?? null,
    })
  }

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return 'bg-green-500'
    if (percentage >= 50) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  const getStatusBadge = (req: TrainingRequirement) => {
    if (req.is_completed) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Completed
        </span>
      )
    }
    if (req.completion_percentage > 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          In Progress
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        Not Started
      </span>
    )
  }

  if (!user) return <LoadingPage />
  if (!isAdminOrHR) return null
  if (isLoading) return <LoadingPage />

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-4">
            <Link to="/learning" className="hover:text-white transition-colors">Learning</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">Training Requirements</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white">Training Requirements</h1>
              <p className="text-red-200 mt-1">Set and manage training hour requirements for employees</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 text-center">
                <p className="text-2xl font-bold text-white">{totalEmployees}</p>
                <p className="text-xs text-red-200">Total</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 text-center">
                <p className="text-2xl font-bold text-green-300">{completedCount}</p>
                <p className="text-xs text-red-200">Completed</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 text-center">
                <p className="text-2xl font-bold text-yellow-300">{inProgressCount}</p>
                <p className="text-xs text-red-200">In Progress</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4 flex-1">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by employee name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>

            {/* Year Filter */}
            <div className="w-full md:w-36">
              <select
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(parseInt(e.target.value))
                  setPage(1)
                }}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-4 py-2.5 bg-white border border-red-600 text-red-600 rounded-xl hover:bg-red-50 transition-colors font-medium"
            >
              Bulk Add
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Requirement
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Required</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Completed</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRequirements.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p>No training requirements found for {yearFilter}</p>
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="mt-4 text-red-600 hover:text-red-700 font-medium"
                      >
                        Add your first requirement
                      </button>
                    </td>
                  </tr>
                ) : (
                  filteredRequirements.map((req) => (
                    <tr
                      key={req.id}
                      onClick={() => navigate(`/learning/requirements/${req.employee.id}`)}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center text-white font-semibold text-sm">
                            {req.employee.full_name?.charAt(0) || req.employee.email.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{req.employee.full_name || 'Unknown'}</p>
                            <p className="text-sm text-gray-500">{req.employee.department || req.employee.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {req.category ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {req.category.name}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            No category
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-900 font-medium">{req.required_hours}h</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-900">{req.completed_hours}h</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden w-24">
                            <div
                              className={`h-full ${getProgressColor(req.completion_percentage)} transition-all duration-300`}
                              style={{ width: `${Math.min(req.completion_percentage, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 w-12">{req.completion_percentage}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(req)}
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(req)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(req)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total} requirements
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm font-medium text-gray-900">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Add Training Requirement</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <select
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="">Select an employee</option>
                  {sortedUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Required Hours</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.required_hours}
                  onChange={(e) => setFormData({ ...formData, required_hours: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category_id || ''}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">No category</option>
                  {sortedCategories.filter(c => c.is_active).map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select a specific category or leave empty for hours from any training
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Bulk Add Training Requirements</h2>
            </div>
            <form onSubmit={handleBulkCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Employees ({bulkFormData.employee_ids.length} selected)
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-2 space-y-1">
                  {sortedUsers.map(u => (
                    <label key={u.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkFormData.employee_ids.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBulkFormData({
                              ...bulkFormData,
                              employee_ids: [...bulkFormData.employee_ids, u.id]
                            })
                          } else {
                            setBulkFormData({
                              ...bulkFormData,
                              employee_ids: bulkFormData.employee_ids.filter(id => id !== u.id)
                            })
                          }
                        }}
                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-900">{u.full_name || u.email}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={bulkFormData.year}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, year: parseInt(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Required Hours (for all)</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={bulkFormData.required_hours}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, required_hours: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={bulkFormData.category_id || ''}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, category_id: e.target.value || null })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">No category</option>
                  {sortedCategories.filter(c => c.is_active).map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select a specific category or leave empty for hours from any training
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBulkModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkCreateMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {bulkCreateMutation.isPending ? 'Creating...' : `Create for ${bulkFormData.employee_ids.length} employees`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingRequirement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Edit Training Requirement</h2>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <p className="text-gray-900">{editingRequirement.employee.full_name || editingRequirement.employee.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <p className="text-gray-900">{editingRequirement.year}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={editFormData.category_id ?? ''}
                  onChange={(e) => setEditFormData({ ...editFormData, category_id: e.target.value || null })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">No category</option>
                  {sortedCategories.filter(c => c.is_active).map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Required Hours</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editFormData.required_hours}
                  onChange={(e) => setEditFormData({ ...editFormData, required_hours: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingRequirement(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete Requirement?</h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to delete the training requirement for{' '}
                <strong>{deleteConfirm.employee.full_name || deleteConfirm.employee.email}</strong>?
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TrainingRequirementsPage
